// Task #3694 — DTO risposta condivisi per gli ordini d'acquisto POS.
// ============================================================================
// SoT unica BE↔FE per gli endpoint consumati da
// packages/frontend/src/pages/pos/PurchaseOrders.tsx:
//   - GET /pos/purchase-orders            → posPurchaseOrderListResponseSchema
//   - GET /pos/purchase-orders/:id        → posPurchaseOrderDetailSchema
//   - GET /pos/suppliers | /pos/suppliers/:id → posPoSupplierRowSchema
//   - GET /pos/warehouses                 → posPoWarehouseRowSchema[]
//
// Convenzioni wire (Task #3694, stile packages/shared/src/schemas/crm.ts):
//   * pg numeric(x,y) e COUNT/SUM senza ::int → union string|number
//     ("pg numeric = stringa sul wire", node-postgres non fa il parse);
//   * COUNT(...)::int / ROUND(...)::int       → z.number();
//   * timestamp/timestamptz → z.union([z.string(), z.date()]) (wire = ISO
//     string; Date per riuso BE pre-serializzazione);
//   * colonne NULLABLE nel DB o alias da LEFT JOIN → .nullable();
//   * jsonb generici → z.unknown();
//   * SELECT alias.* → colonne note enumerate + .passthrough() (colonne future).
// ============================================================================

import { z } from 'zod';
import { refineVatTriplet } from '../fiscal/vat-triplet';
import { documentNumberingInputFields, documentNumberingModeSchema, refineDocumentNumbering } from './document-numbering';
import { posPurchaseOrderCostSuggestionSchema } from './purchase-costs';
import {
  posDocumentScannedUnitSchema,
  posDocumentScannerStateSchema,
  type PosDocumentScannerState,
} from './document-scanner';
import { posAttachmentListSchema } from './attachments';

/** pg numeric(x,y) senza cast: stringa sul wire, number se BE pre-serializza. */
const wireNumeric = z.union([z.string(), z.number()]);
const maxPurchaseOrderNumeric = 99_999_999.9999;

/** timestamptz: ISO string sul wire, Date lato BE pre-serializzazione. */
const wireTimestamp = z.union([z.string(), z.date()]);

/**
 * Opaque PostgreSQL CAS token. Keep all six fractional digits: converting this
 * value through JavaScript Date would discard precision and cause false CAS
 * matches/conflicts.
 */
export const posPurchaseOrderRevisionSchema = z.string().regex(
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/,
  'Revisione ordine non valida.',
);
export type PosPurchaseOrderRevision = z.infer<typeof posPurchaseOrderRevisionSchema>;

/**
 * Riga scrivibile PO. `unit_price` è sempre il prezzo documentale
 * pre-sconto (IVA esclusa); null conserva un prezzo non ancora noto, mentre
 * zero è un valore commerciale valido. `unit_cost` non è un alias accettato.
 */
export const posPurchaseOrderItemInputSchema = z.object({
  /** Required for an existing row on update/autosave; omitted only for a new row. */
  item_id: z.string().uuid().optional(),
  product_id: z.string().min(1),
  /** Optional immutable supplier-code snapshot selected for this PO line. */
  supplier_sku: z.string().trim().max(255).nullable().optional(),
  // Historical rows may be fractional; newly entered quantities are checked
  // as integers by the application layer without invalidating that history.
  quantity_ordered: z.coerce.number().finite().positive().max(maxPurchaseOrderNumeric),
  unit_price: z.coerce.number().finite().nonnegative().max(maxPurchaseOrderNumeric).nullable(),
  cost_suggestion: posPurchaseOrderCostSuggestionSchema.nullable().optional(),
  discount_percent: z.coerce.number().finite().min(0).max(100),
  vat_rate: z.coerce.number().finite().min(0).max(100),
  vat_nature: z.string().trim().min(1).max(10).nullable().optional(),
  riferimento_normativo: z.string().trim().min(1).max(160).nullable().optional(),
  unit_of_measure: z.string().max(20).nullable().optional(),
  variant_id: z.string().uuid().nullable().optional(),
  lot_number: z.string().max(100).nullable().optional(),
  production_date: z.string().max(64).nullable().optional(),
  expiry_date: z.string().max(64).nullable().optional(),
  notes: z.string().nullable().optional(),
  /** Scanner counting metadata; omission preserves it on an existing line. */
  scanned_units: z.array(posDocumentScannedUnitSchema).max(3000).optional(),
}).strict().superRefine((item, ctx) => {
  // Regola unica della terna (shared `vat-triplet`): natura solo a IVA 0%.
  // Righe storiche (item_id presente) senza natura restano leggibili e
  // scrivibili invariate; il riferimento normativo è storia documentale, non
  // input di ordine, quindi la policy PO richiede la sola natura.
  refineVatTriplet(item, ctx, { zeroRate: 'nature', legacyZeroRateRow: item.item_id !== undefined });
});
export type PosPurchaseOrderItemInput = z.infer<typeof posPurchaseOrderItemInputSchema>;

export const posPurchaseOrderTransportModeSchema = z.enum([
  'mittente',
  'destinatario',
  'vettore',
  'ritiro',
]);
export type PosPurchaseOrderTransportMode = z.infer<typeof posPurchaseOrderTransportModeSchema>;

/**
 * Raw shared PO object shape. Consumers which append an atomic-operation key
 * must extend this object before applying the common cross-field refinement.
 */
export const posPurchaseOrderBodyObjectSchema = z.object({
  expected_revision: posPurchaseOrderRevisionSchema.optional(),
  ...documentNumberingInputFields,
  supplier_id: z.string().uuid().nullish(),
  warehouse_id: z.string().uuid().nullish(),
  order_date: z.string().date().nullish(),
  expected_delivery: z.string().date().nullish(),
  payment_terms: z.string().max(1024).nullish(),
  notes: z.string().max(20000).nullish(),
  reference: z.string().max(255).nullish(),
  /**
   * Riferimenti agli allegati GIÀ caricati sull'ordine tramite l'endpoint
   * dedicato (chiave provider con prefisso tenant/ordine). Il service rifiuta
   * ogni voce non presente fra quelle persistite: create/update possono solo
   * riordinare o togliere, mai introdurre file o url liberi.
   */
  attachments: posAttachmentListSchema.max(50).optional(),
  /** Omission preserves the draft scanner state on update. */
  scanner_state: posDocumentScannerStateSchema.optional(),
  transport_mode: posPurchaseOrderTransportModeSchema.nullish(),
  carrier_id: z.string().uuid().nullish(),
  items: z.array(posPurchaseOrderItemInputSchema).optional(),
  autosave: z.boolean().optional(),
}).strict();

export function refinePosPurchaseOrderTransport(
  order: z.infer<typeof posPurchaseOrderBodyObjectSchema>,
  ctx: z.RefinementCtx,
): void {
  refineDocumentNumbering(order, ctx);
  if (order.transport_mode === 'vettore' && !order.carrier_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['carrier_id'],
      message: 'Il vettore è obbligatorio quando la modalità di trasporto è tramite vettore.',
    });
  }
}

/** Shared write contract for ordinary draft create/update PO endpoints. */
export const posPurchaseOrderBodySchema = posPurchaseOrderBodyObjectSchema
  .superRefine(refinePosPurchaseOrderTransport);
export type PosPurchaseOrderInput = z.infer<typeof posPurchaseOrderBodySchema>;

/**
 * Purchase orders describe quantities to buy, not physical units already in
 * stock.  Legacy drafts may still contain an unfinished serial collector from
 * the former workflow; make that collector non-blocking without touching line
 * quantities or the historical `scanned_units` snapshots.
 */
export function normalizePurchaseOrderScannerState(
  state: PosDocumentScannerState,
): PosDocumentScannerState {
  if (state?.pending?.kind !== 'serials') return state;
  const { serial: _legacySerial, code: _legacyCode, ...legacyResolution } = state.pending.resolution;
  const {
    serial_id: _legacyProductSerialId,
    serial_number: _legacyProductSerialNumber,
    unit_id: _legacyProductUnitId,
    ...product
  } = legacyResolution.product;
  const resolution = {
    ...legacyResolution,
    product,
    document_policy: 'purchase_order' as const,
  };
  return {
    ...state,
    legacy_order_serials: state.legacy_order_serials ?? state.pending,
    pending: {
      kind: 'quantity',
      resolution,
      quantityText: state.pending.serials.length > 0
        ? String(state.pending.serials.length)
        : '',
    },
  };
}

/** Preview must follow the same warehouse issuer and document year as creation. */
export const posPurchaseOrderNumberPreviewQuerySchema = z.object({
  warehouse_id: z.string().uuid().optional(),
  order_date: z.string().date().optional(),
}).strict();
export type PosPurchaseOrderNumberPreviewQuery = z.infer<typeof posPurchaseOrderNumberPreviewQuerySchema>;
export const posPurchaseOrderNumberPreviewSchema = z.object({ order_number: z.string() });
export type PosPurchaseOrderNumberPreview = z.infer<typeof posPurchaseOrderNumberPreviewSchema>;

/**
 * The service requires the token on every update. Allow omission through HTTP
 * parsing so old clients get the typed 428 precondition error, not a generic
 * validation error that cannot offer the conflict/reopen recovery path.
 */
export const posPurchaseOrderUpdateBodySchema = posPurchaseOrderBodyObjectSchema
  .extend({
    expected_revision: posPurchaseOrderRevisionSchema.optional(),
  })
  .strict()
  .superRefine(refinePosPurchaseOrderTransport);
export type PosPurchaseOrderUpdateInput = z.infer<typeof posPurchaseOrderUpdateBodySchema>;

/**
 * Keep the PO line identity and optional logistics metadata when a parsed
 * HTTP line is handed to the service.  This deliberately enumerates the
 * fields instead of relying on a spread at the route boundary: dropping an
 * optional `item_id` would make autosave look like a delete followed by an
 * insert, while TypeScript would still consider the resulting object valid.
 */
export function normalizePosPurchaseOrderItemInput(
  item: PosPurchaseOrderItemInput,
): PosPurchaseOrderItemInput {
  return {
    item_id: item.item_id,
    product_id: item.product_id,
    ...(item.supplier_sku !== undefined ? { supplier_sku: item.supplier_sku } : {}),
    quantity_ordered: item.quantity_ordered,
    unit_price: item.unit_price,
    ...(item.cost_suggestion !== undefined ? { cost_suggestion: item.cost_suggestion } : {}),
    discount_percent: item.discount_percent,
    vat_rate: item.vat_rate,
    vat_nature: item.vat_nature,
    riferimento_normativo: item.riferimento_normativo,
    unit_of_measure: item.unit_of_measure,
    variant_id: item.variant_id,
    lot_number: item.lot_number,
    production_date: item.production_date,
    expiry_date: item.expiry_date,
    notes: item.notes,
    ...(item.scanned_units !== undefined ? { scanned_units: item.scanned_units } : {}),
  };
}

/**
 * Riga di ricevimento PO: l'identità è obbligatoriamente la riga ordine. Il
 * prezzo/sconto sono eventuali override documentali pre-sconto, non costi
 * netti e non accettiamo il vecchio campo `unit_cost`.
 */
export const posPurchaseOrderReceiveItemSchema = z.object({
  item_id: z.string().uuid(),
  quantity_received: z.coerce.number().nonnegative().optional(),
  quantity: z.coerce.number().nonnegative().optional(),
  lot_number: z.string().max(100).nullable().optional(),
  production_date: z.string().max(64).nullable().optional(),
  expiry_date: z.string().max(64).nullable().optional(),
  variant_id: z.string().uuid().nullable().optional(),
  serial_numbers: z.array(z.string().min(1)).max(1000).optional(),
  unit_price: z.coerce.number().nonnegative().nullable().optional(),
  discount_percent: z.coerce.number().min(0).max(100).nullable().optional(),
}).strict();
export type PosPurchaseOrderReceiveItem = z.infer<typeof posPurchaseOrderReceiveItemSchema>;

/** Shared DDT/PO receipt fields; strict so obsolete `unit_cost` is rejected. */
export const posGoodsReceiveLineSchema = z.object({
  id: z.string().uuid().optional(),
  item_id: z.string().uuid().optional(),
  product_id: z.string().uuid().nullable().optional(),
  product_name: z.string().max(255).nullable().optional(),
  sku: z.string().max(100).nullable().optional(),
  quantity: z.coerce.number().optional(),
  quantity_received: z.coerce.number().optional(),
  unit_of_measure: z.string().max(20).optional(),
  unit_price: z.coerce.number().nonnegative().nullable().optional(),
  discount_percent: z.coerce.number().min(0).max(100).nullable().optional(),
  vat_rate: z.coerce.number().min(0).max(100).optional(),
  lot_number: z.string().max(100).nullable().optional(),
  expiry_date: z.string().max(64).nullable().optional(),
  variant_id: z.string().uuid().nullable().optional(),
  serial_numbers: z.array(z.string().min(1)).max(1000).optional(),
  serials: z.array(z.object({
    kind: z.string().min(1).optional(),
    serial_number: z.string().min(1),
  }).strict()).max(3000).optional(),
  supplier_sku: z.string().max(255).optional(),
  supplier_product_code: z.string().max(255).nullable().optional(),
  /**
   * A physical receipt quota names source document rows through `allocations`;
   * this generic UI row deliberately has no scalar PO-line compatibility alias.
   */
}).strict();

export const posGoodsReceiveBodySchema = z.object({
  warehouse_id: z.string().max(255).nullish(),
  items: z.array(posGoodsReceiveLineSchema).optional(),
}).passthrough();

export const posPurchaseOrderReceiveBodySchema = z.object({
  warehouse_id: z.string().max(255).nullish(),
  items: z.array(posPurchaseOrderReceiveItemSchema).optional(),
}).passthrough();

// ============================================================================
// 1. Riga item ordine d'acquisto
// GET /pos/purchase-orders/:id items:
//   SELECT poi.*, p.name as product_name_joined, p.sku as product_sku,
//          COALESCE(p.product_nature,'product') as product_nature
// Colonne enumerate da pos_purchase_order_items (schema.sql:18335-18352).
// ============================================================================
export const posPurchaseOrderItemRowSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    purchase_order_id: z.string(),
    product_id: z.string().nullable(), // uuid NULL (prodotto non collegato)
    product_name: z.string().nullable(), // varchar(255) NULL
    sku: z.string().nullable(), // varchar(100) NULL
    quantity_ordered: wireNumeric.nullable(), // numeric(12,4) DEFAULT 0
    quantity_received: wireNumeric.nullable(), // numeric(12,4) DEFAULT 0
    unit_price: wireNumeric.nullable(), // numeric(12,4) DEFAULT 0
    cost_suggestion: posPurchaseOrderCostSuggestionSchema.nullable().optional(),
    vat_rate: wireNumeric.nullable(), // numeric(5,2) DEFAULT 22
    vat_nature: z.string().nullable().optional(),
    riferimento_normativo: z.string().nullable().optional(),
    line_net: wireNumeric.nullable(), // numeric(12,4) DEFAULT 0
    line_tax: wireNumeric.nullable(), // numeric(12,4) DEFAULT 0
    line_total: wireNumeric.nullable(), // numeric(12,4) DEFAULT 0
    notes: z.string().nullable(), // text NULL
    created_at: wireTimestamp, // timestamptz NOT NULL
    discount_percent: wireNumeric, // numeric(5,2) NOT NULL DEFAULT 0
     unit_of_measure: z.string().nullable().optional(),
     variant_id: z.string().nullable().optional(),
     supplier_sku: z.string().nullable().optional(),
     lot_number: z.string().nullable().optional(),
     production_date: z.string().nullable().optional(),
     expiry_date: z.string().nullable().optional(),
     scanned_units: z.array(posDocumentScannedUnitSchema),
    // Alias da LEFT JOIN pos_products → null se prodotto mancante.
    product_name_joined: z.string().nullable(), // p.name
    product_sku: z.string().nullable(), // p.sku
    /**
     * Live catalog lookup used only when composing a new downstream document.
     * For a variant row this is the variant EAN (including null), never the
     * parent product EAN and never historical purchase-order evidence.
     */
    current_catalog_ean: z.string().nullable().optional(),
    product_nature: z.string(), // COALESCE(p.product_nature,'product') → sempre valorizzato
  })
  .passthrough();
export type PosPurchaseOrderItemRow = z.infer<typeof posPurchaseOrderItemRowSchema>;

// ============================================================================
// 2. Riga lista ordini d'acquisto
// GET /pos/purchase-orders:
//   SELECT po.*, s.name as supplier_name_joined, s.email as supplier_email,
//          w.name as warehouse_name, COALESCE(...ROUND(...)::int,0) AS completion_pct
// Colonne enumerate da pos_purchase_orders (schema.sql:18361-18383).
// ============================================================================
export const posPurchaseOrderListRowSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    order_number: z.string(), // varchar(50) NOT NULL
    numbering_mode: documentNumberingModeSchema.optional(),
    supplier_id: z.string().nullable(), // uuid NULL
    supplier_name: z.string().nullable(), // varchar(255) NULL (denormalizzato)
    status: z.string().nullable(), // varchar(50) NULL DEFAULT 'bozza'
    order_date: wireTimestamp.nullable(), // timestamptz NULL DEFAULT now()
    expected_delivery: wireTimestamp.nullable(), // timestamptz NULL
    warehouse_id: z.string().nullable(), // uuid NULL
    subtotal: wireNumeric.nullable(), // numeric(12,4) NULL DEFAULT 0
    tax_amount: wireNumeric.nullable(), // numeric(12,4) NULL DEFAULT 0
    total: wireNumeric.nullable(), // numeric(12,4) NULL DEFAULT 0
    notes: z.string().nullable(), // text NULL
    created_by: z.string().nullable(), // uuid NULL
    created_at: wireTimestamp, // timestamptz NOT NULL
    updated_at: wireTimestamp, // timestamptz NOT NULL
    /** Opaque microsecond-precision token used by conditional writes. */
    revision: posPurchaseOrderRevisionSchema.optional(),
    payment_terms: z.string().nullable(), // varchar(100) NULL
    sent_to_supplier_at: wireTimestamp.nullable(), // timestamptz NULL
    attachments: posAttachmentListSchema, // jsonb NOT NULL DEFAULT '[]' (voci provider-managed)
    scanner_state: posDocumentScannerStateSchema,
    autosaved_at: wireTimestamp.nullable(), // timestamptz NULL
    company_id: z.string(), // uuid NOT NULL
    transport_mode: posPurchaseOrderTransportModeSchema.nullable().optional(),
    carrier_id: z.string().nullable().optional(),
    carrier_name: z.string().nullable().optional(),
    // Alias da LEFT JOIN → null se supplier/warehouse mancante.
    supplier_name_joined: z.string().nullable(), // s.name
    supplier_email: z.string().nullable(), // s.email
    warehouse_name: z.string().nullable(), // w.name
    completion_pct: z.number(), // ROUND(...)::int → intero (0 di default)
  })
  .passthrough();
export type PosPurchaseOrderListRow = z.infer<typeof posPurchaseOrderListRowSchema>;

export const posPoCarrierRowSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  code: z.string().nullable(),
  name: z.string(),
  is_active: z.boolean(),
}).passthrough();
export type PosPoCarrierRow = z.infer<typeof posPoCarrierRowSchema>;

// ============================================================================
// 3. Testata dettaglio ordine d'acquisto
// GET /pos/purchase-orders/:id header: come la riga lista + alias anagrafica
// fornitore (supplier_vat/address/city/zip/province) + items[].
// Mapper BE: { ...headerRes.rows[0], items: itemsRes.rows }.
// ============================================================================
export const posPurchaseOrderDetailSchema = posPurchaseOrderListRowSchema
  .extend({
    revision: posPurchaseOrderRevisionSchema,
    // Alias da LEFT JOIN pos_suppliers → null se fornitore mancante.
    supplier_vat: z.string().nullable(), // s.vat_number
    supplier_address: z.string().nullable(), // s.address
    supplier_city: z.string().nullable(), // s.city
    supplier_zip: z.string().nullable(), // s.zip
    supplier_province: z.string().nullable(), // s.province
    warehouse_address: z.string().nullable().optional(),
    warehouse_city: z.string().nullable().optional(),
    warehouse_zip: z.string().nullable().optional(),
    items: z.array(posPurchaseOrderItemRowSchema),
  })
  .passthrough();
export type PosPurchaseOrderDetail = z.infer<typeof posPurchaseOrderDetailSchema>;

// ============================================================================
// 4. Envelope paginato lista ordini
// paginatedResponse (response.ts:57-89): { success, data:{ items, pagination },
// meta:{...} }. Il FE legge res.data.items e (res as any).pagination: il wire
// VERO annida pagination sotto data (apiClient può flatten, ma il contratto BE
// resta questo). Modelliamo la shape servita dal backend.
// ============================================================================
export const posPurchaseOrderPaginationSchema = z.object({
  page: z.number(),
  limit: z.number(),
  total: z.number(), // helper tipizza number; COUNT pg è stringa a runtime → union
  totalPages: z.number(),
});
export type PosPurchaseOrderPagination = z.infer<typeof posPurchaseOrderPaginationSchema>;

export const posPurchaseOrderListResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    items: z.array(posPurchaseOrderListRowSchema),
    // total da COUNT(*) senza ::int → stringa sul wire nonostante il tipo helper.
    pagination: posPurchaseOrderPaginationSchema.extend({
      total: z.union([z.string(), z.number()]), // "pg COUNT = stringa sul wire"
    }),
  }),
  meta: z
    .object({
      timestamp: wireTimestamp,
      request_id: z.string(),
      tenant_id: z.string().optional(),
    })
    .passthrough(),
});
export type PosPurchaseOrderListResponse = z.infer<typeof posPurchaseOrderListResponseSchema>;
export type PosPurchaseOrderListData = PosPurchaseOrderListResponse['data'];

// ============================================================================
// 5. Riga fornitore
// GET /pos/suppliers | /pos/suppliers/:id:
//   SELECT *, (managed_by_org_id IS NOT NULL) AS is_locked FROM pos_suppliers
// Colonne enumerate da pos_suppliers (schema.sql) + is_locked (isLockedSql).
// ============================================================================
export const posPoSupplierRowSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    code: z.string().nullable(), // varchar(50) NULL
    name: z.string(), // varchar(255) NOT NULL
    vat_number: z.string().nullable(), // varchar(20) NULL
    fiscal_code: z.string().nullable(), // varchar(20) NULL
    address: z.string().nullable(), // text NULL
    city: z.string().nullable(), // varchar(100) NULL
    province: z.string().nullable(), // varchar(10) NULL
    zip: z.string().nullable(), // varchar(10) NULL
    country: z.string().nullable(), // varchar(100) NULL DEFAULT 'Italia'
    phone: z.string().nullable(), // varchar(50) NULL
    email: z.string().nullable(), // varchar(255) NULL
    pec: z.string().nullable(), // varchar(255) NULL
    iban: z.string().nullable(), // varchar(34) NULL
    bank_name: z.string().nullable(), // varchar(255) NULL
    payment_terms: z.string().nullable(), // varchar(100) NULL DEFAULT '30gg'
    notes: z.string().nullable(), // text NULL
    is_active: z.boolean(), // boolean NOT NULL DEFAULT true
    created_at: wireTimestamp, // timestamptz NOT NULL
    updated_at: wireTimestamp, // timestamptz NOT NULL
    legal_entity_id: z.string().nullable(), // uuid NULL
    legal_form: z.string().nullable(), // varchar(50) NULL
    trade_name: z.string().nullable(), // varchar(255) NULL
    sdi_code: z.string().nullable(), // varchar(20) NULL
    bic: z.string().nullable(), // varchar(11) NULL
    website: z.string().nullable(), // varchar(255) NULL
    managed_by_org_id: z.string().nullable(), // uuid NULL
    retail_brand_id: z.string().nullable(), // uuid NULL
    is_locked: z.boolean(), // (managed_by_org_id IS NOT NULL) AS is_locked
  })
  .passthrough();
export type PosPoSupplierRow = z.infer<typeof posPoSupplierRowSchema>;

// ============================================================================
// 6. Riga magazzino
// GET /pos/warehouses:
//   SELECT w.*, s.name AS store_name, EXISTS(...) AS has_stock,
//          COALESCE(json_agg(... id,name,address,city,postal_code,is_reference ...),'[]') AS stores
// Colonne enumerate da pos_warehouses (schema.sql) + alias calcolati.
// ============================================================================
/** Voce dell'aggregato `stores` (json_build_object da pos_warehouse_stores⋈stores). */
export const posPoWarehouseStoreSchema = z
  .object({
    id: z.string(), // st.id
    name: z.string(), // st.name (NOT NULL)
    address: z.string().nullable(), // st.address (text NULL)
    city: z.string().nullable(), // st.city (varchar(100) NULL)
    postal_code: z.string().nullable(), // st.postal_code (varchar(20) NULL)
    is_reference: z.boolean().nullable(), // ws.is_reference
  })
  .passthrough();
export type PosPoWarehouseStore = z.infer<typeof posPoWarehouseStoreSchema>;

export const posPoWarehouseRowSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    code: z.string().nullable(), // varchar(50) NULL
    name: z.string(), // varchar(255) NOT NULL
    address: z.string().nullable(), // text NULL
    city: z.string().nullable(), // varchar(100) NULL
    is_default: z.boolean(), // boolean NOT NULL DEFAULT false
    is_active: z.boolean(), // boolean NOT NULL DEFAULT true
    created_at: wireTimestamp, // timestamptz NOT NULL
    updated_at: wireTimestamp, // timestamptz NOT NULL
    store_id: z.string().nullable(), // uuid NULL
    zip_code: z.string().nullable(), // varchar(20) NULL
    company_id: z.string(), // uuid NOT NULL
    // Alias calcolati.
    store_name: z.string().nullable(), // s.name da LEFT JOIN stores → null se assente
    has_stock: z.boolean(), // EXISTS(...) → boolean
    stores: z.array(posPoWarehouseStoreSchema), // COALESCE(json_agg,'[]') → sempre array
  })
  .passthrough();
export type PosPoWarehouseRow = z.infer<typeof posPoWarehouseRowSchema>;
